"use client"

import { useState, useRef, useCallback, useEffect } from "react"
import {
  Search,
  Play,
  Diamond,
  Circle,
  Zap,
  Bell,
  Code,
  Mail,
  X,
  Save,
  Link,
  Trash2,
  Plus,
  Settings,
  Truck,
  Package,
  Thermometer,
  Clock,
  CheckCircle,
  User,
  Wrench,
  Wifi,
  Target,
  Activity,
  Layers,
  Filter,
  Eye,
  ChevronLeft,
  ChevronRight,
  Pause,
  PlayCircle,
  Edit,
  TrendingUp,
  Loader2,
  FileText,
} from "lucide-react"

const nodeTypes = [
  // Triggers - Disparadores
  {
    type: "truck_arrival",
    name: "Llegada de Camión",
    description: "Se detecta la llegada de un camión",
    icon: Truck,
    color: "bg-blue-500",
    category: "triggers",
  },
  {
    type: "low_stock",
    name: "Stock Bajo",
    description: "Producto por debajo del mínimo",
    icon: Package,
    color: "bg-orange-500",
    category: "triggers",
  },
  {
    type: "temperature_alert",
    name: "Alerta Temperatura",
    description: "Temperatura fuera del rango",
    icon: Thermometer,
    color: "bg-red-500",
    category: "triggers",
  },
  {
    type: "schedule_trigger",
    name: "Programación",
    description: "Ejecutar en horario específico",
    icon: Clock,
    color: "bg-purple-500",
    category: "triggers",
  },
  {
    type: "sensor_trigger",
    name: "Sensor IoT",
    description: "Activación por sensor inteligente",
    icon: Wifi,
    color: "bg-indigo-500",
    category: "triggers",
  },
  {
    type: "manual_trigger",
    name: "Activación Manual",
    description: "Iniciado por operador",
    icon: User,
    color: "bg-gray-600",
    category: "triggers",
  },

  // Conditions - Condiciones
  {
    type: "condition",
    name: "Condición",
    description: "Evalúa una condición específica",
    icon: Diamond,
    color: "bg-blue-600",
    category: "conditions",
  },

  // Actions - Acciones
  {
    type: "move_product",
    name: "Mover Producto",
    description: "Traslada producto a otra ubicación",
    icon: Package,
    color: "bg-green-500",
    category: "actions",
  },
  {
    type: "assign_task",
    name: "Asignar Tarea",
    description: "Asigna tarea a un operador",
    icon: User,
    color: "bg-indigo-500",
    category: "actions",
  },
  {
    type: "activate_equipment",
    name: "Activar Equipo",
    description: "Enciende equipos o maquinaria",
    icon: Wrench,
    color: "bg-yellow-600",
    category: "actions",
  },
  {
    type: "generate_report",
    name: "Generar Reporte",
    description: "Crea reportes automáticos",
    icon: FileText,
    color: "bg-blue-700",
    category: "actions",
  },
  {
    type: "adjust_temperature",
    name: "Ajustar Temperatura",
    description: "Modifica configuración térmica",
    icon: Thermometer,
    color: "bg-red-600",
    category: "actions",
  },

  // Notifications - Notificaciones
  {
    type: "notification",
    name: "Notificación",
    description: "Envía una notificación",
    icon: Bell,
    color: "bg-yellow-500",
    category: "notifications",
  },
  {
    type: "email",
    name: "Email",
    description: "Envía un email",
    icon: Mail,
    color: "bg-red-500",
    category: "notifications",
  },
  {
    type: "dashboard_alert",
    name: "Alerta Dashboard",
    description: "Muestra alerta en dashboard",
    icon: Activity,
    color: "bg-pink-500",
    category: "notifications",
  },
  {
    type: "sms_alert",
    name: "SMS",
    description: "Envía mensaje de texto",
    icon: Bell,
    color: "bg-green-500",
    category: "notifications",
  },

  // Helpers - Ayudas
  {
    type: "delay",
    name: "Espera",
    description: "Pausa la ejecución",
    icon: Clock,
    color: "bg-gray-500",
    category: "helpers",
  },
  {
    type: "code",
    name: "Código Personalizado",
    description: "Ejecuta código personalizado",
    icon: Code,
    color: "bg-gray-600",
    category: "helpers",
  },
  {
    type: "loop",
    name: "Bucle",
    description: "Repite acciones múltiples veces",
    icon: Target,
    color: "bg-indigo-600",
    category: "helpers",
  },
  {
    type: "filter",
    name: "Filtro",
    description: "Filtra datos según criterios",
    icon: Filter,
    color: "bg-teal-600",
    category: "helpers",
  },
]

export default function ReglaAutomatizacion() {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const [activeView, setActiveView] = useState("automations")
  const [nodes, setNodes] = useState([])

  const [connections, setConnections] = useState([])
  const [selectedNode, setSelectedNode] = useState(null)
  const [selectedConnection, setSelectedConnection] = useState(null)
  const [connectingFrom, setConnectingFrom] = useState(null)
  const [connectionMode, setConnectionMode] = useState(false)
  const [draggedNode, setDraggedNode] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [activeCategory, setActiveCategory] = useState("triggers")
  const [automationName, setAutomationName] = useState("Nueva Automatización")
  const [automationDescription, setAutomationDescription] = useState("")
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [editingAutomation, setEditingAutomation] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [lastSaved, setLastSaved] = useState(null)
  const canvasRef = useRef(null)

  // Estado para formularios de configuración
  const [nodeForm, setNodeForm] = useState({
    name: "",
    description: "",
    config: {},
  })

  const [connectionForm, setConnectionForm] = useState({
    label: "",
    condition: "",
  })

  // Automatizaciones guardadas con datos más realistas
  const [savedAutomations, setSavedAutomations] = useState([
    {
      id: "auto_001",
      name: "Control Temperatura Zona Fría",
      description: "Monitorea temperatura en cámaras frigoríficas y activa sistemas de emergencia",
      status: "active",
      nodeCount: 6,
      connectionCount: 5,
      lastRun: new Date("2024-08-26T10:30:00"),
      executions: 1247,
      successRate: 98.3,
      createdAt: new Date("2024-07-15T09:00:00"),
      category: "Control Ambiental",
      priority: "alta",
      executionTime: "0.45s",
      nextRun: new Date("2024-08-26T11:00:00"),
    },
    {
      id: "auto_002",
      name: "Reposición Automática Stock",
      description: "Detecta niveles críticos de inventario y genera órdenes automáticamente",
      status: "active",
      nodeCount: 8,
      connectionCount: 7,
      lastRun: new Date("2024-08-26T09:15:00"),
      executions: 856,
      successRate: 94.7,
      createdAt: new Date("2024-07-20T14:30:00"),
      category: "Inventario",
      priority: "media",
      executionTime: "1.2s",
      nextRun: new Date("2024-08-26T12:00:00"),
    },
    {
      id: "auto_003",
      name: "Proceso Recepción Mercancía",
      description: "Automatiza verificación, etiquetado y ubicación de productos entrantes",
      status: "paused",
      nodeCount: 12,
      connectionCount: 11,
      lastRun: new Date("2024-08-25T16:45:00"),
      executions: 423,
      successRate: 96.8,
      createdAt: new Date("2024-07-10T11:20:00"),
      category: "Logística",
      priority: "alta",
      executionTime: "2.1s",
      nextRun: null,
    },
    {
      id: "auto_004",
      name: "Mantenimiento Predictivo",
      description: "Programa mantenimiento basado en sensores IoT de equipos críticos",
      status: "draft",
      nodeCount: 9,
      connectionCount: 8,
      lastRun: null,
      executions: 0,
      successRate: 0,
      createdAt: new Date("2024-08-20T12:00:00"),
      category: "Mantenimiento",
      priority: "baja",
      executionTime: "--",
      nextRun: null,
    },
    {
      id: "auto_005",
      name: "Alertas Seguridad Nocturna",
      description: "Sistema de monitoreo y respuesta automática durante horario nocturno",
      status: "active",
      nodeCount: 7,
      connectionCount: 6,
      lastRun: new Date("2024-08-26T02:30:00"),
      executions: 234,
      successRate: 99.1,
      createdAt: new Date("2024-08-01T08:00:00"),
      category: "Seguridad",
      priority: "alta",
      executionTime: "0.8s",
      nextRun: new Date("2024-08-27T00:00:00"),
    },
    {
      id: "auto_006",
      name: "Optimización Rutas Picking",
      description: "Calcula rutas óptimas para preparación de pedidos en tiempo real",
      status: "active",
      nodeCount: 10,
      connectionCount: 9,
      lastRun: new Date("2024-08-26T08:45:00"),
      executions: 1456,
      successRate: 97.2,
      createdAt: new Date("2024-06-30T10:15:00"),
      category: "Optimización",
      priority: "media",
      executionTime: "0.8s",
      nextRun: new Date("2024-08-26T13:00:00"),
    },
    {
      id: "auto_007",
      name: "Control Calidad Automático",
      description: "Inspección automática con cámaras IA y sensores de calidad",
      status: "active",
      nodeCount: 14,
      connectionCount: 13,
      lastRun: new Date("2024-08-26T11:20:00"),
      executions: 892,
      successRate: 99.5,
      createdAt: new Date("2024-08-05T14:00:00"),
      category: "Calidad",
      priority: "alta",
      executionTime: "1.5s",
      nextRun: new Date("2024-08-26T14:00:00"),
    },
    {
      id: "auto_008",
      name: "Gestión Energética Inteligente",
      description: "Optimiza consumo energético según demanda y horarios",
      status: "paused",
      nodeCount: 11,
      connectionCount: 10,
      lastRun: new Date("2024-08-25T23:30:00"),
      executions: 567,
      successRate: 95.8,
      createdAt: new Date("2024-07-25T16:45:00"),
      category: "Sostenibilidad",
      priority: "media",
      executionTime: "0.6s",
      nextRun: null,
    },
  ])

  const filteredNodes = nodeTypes.filter(
    (node) => node.category === activeCategory && node.name.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  // Sincronizar formulario cuando se selecciona un nodo
  useEffect(() => {
    if (selectedNode) {
      setNodeForm({
        name: selectedNode.name,
        description: selectedNode.description,
        config: { ...selectedNode.config },
      })
    }
  }, [selectedNode])

  // Sincronizar formulario cuando se selecciona una conexión
  useEffect(() => {
    if (selectedConnection) {
      setConnectionForm({
        label: selectedConnection.label || "",
        condition: selectedConnection.condition || "",
      })
    }
  }, [selectedConnection])

  const handleDragStart = (nodeType) => {
    setDraggedNode(nodeType)
  }

  const handleCanvasDrop = useCallback(
    (e) => {
      e.preventDefault()
      if (!draggedNode || !canvasRef.current) return

      const rect = canvasRef.current.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      const nodeType = nodeTypes.find((n) => n.type === draggedNode)
      if (!nodeType) return

      const newNode = {
        id: `node-${Date.now()}`,
        type: draggedNode,
        name: nodeType.name,
        description: nodeType.description,
        position: { x: x - 75, y: y - 25 },
        config: {},
        connections: [],
      }

      setNodes((prev) => [...prev, newNode])
      setDraggedNode(null)
    },
    [draggedNode],
  )

  const handleNodeClick = (node, e) => {
    e.stopPropagation()

    if (connectionMode) {
      if (connectingFrom === null) {
        setConnectingFrom(node.id)
      } else if (connectingFrom !== node.id) {
        const newConnection = {
          id: `conn-${Date.now()}`,
          from: connectingFrom,
          to: node.id,
          label: "",
          condition: "",
        }
        setConnections((prev) => [...prev, newConnection])
        setConnectingFrom(null)
        setConnectionMode(false)
      }
    } else {
      setSelectedNode(node)
      setSelectedConnection(null)
    }
  }

  const handleNodeMouseDown = (node, e) => {
    if (connectionMode) return

    setIsDragging(true)
    setSelectedNode(node)
    const rect = canvasRef.current.getBoundingClientRect()
    setDragOffset({
      x: e.clientX - rect.left - node.position.x,
      y: e.clientY - rect.top - node.position.y,
    })
  }

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging || !selectedNode || !canvasRef.current) return

      const rect = canvasRef.current.getBoundingClientRect()
      const newX = e.clientX - rect.left - dragOffset.x
      const newY = e.clientY - rect.top - dragOffset.y

      setNodes((prev) =>
        prev.map((node) =>
          node.id === selectedNode.id ? { ...node, position: { x: Math.max(0, newX), y: Math.max(0, newY) } } : node,
        ),
      )
    },
    [isDragging, selectedNode, dragOffset],
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseup", handleMouseUp)
      return () => {
        document.removeEventListener("mousemove", handleMouseMove)
        document.removeEventListener("mouseup", handleMouseUp)
      }
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  const getConnectionPath = (fromNode, toNode) => {
    const fromX = fromNode.position.x + 75
    const fromY = fromNode.position.y + 25
    const toX = toNode.position.x + 75
    const toY = toNode.position.y + 25

    const midX = (fromX + toX) / 2
    const midY = (fromY + toY) / 2

    return `M ${fromX} ${fromY} Q ${midX} ${midY - 50} ${toX} ${toY}`
  }

  const createArrowMarker = () => (
    <defs>
      <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#6b7280" />
      </marker>
    </defs>
  )

  // Actualizar nodo con los valores del formulario
  const updateNode = (field, value) => {
    const updatedForm = { ...nodeForm }
    if (field === "name" || field === "description") {
      updatedForm[field] = value
    } else {
      updatedForm.config[field] = value
    }
    setNodeForm(updatedForm)

    // Actualizar el nodo en el estado
    setNodes((prev) =>
      prev.map((node) =>
        node.id === selectedNode.id
          ? {
              ...node,
              [field === "name" || field === "description" ? field : "config"]:
                field === "name" || field === "description" ? value : { ...node.config, [field]: value },
            }
          : node,
      ),
    )

    // Actualizar selectedNode para mantener sincronización
    setSelectedNode((prev) =>
      prev
        ? {
            ...prev,
            [field === "name" || field === "description" ? field : "config"]:
              field === "name" || field === "description" ? value : { ...prev.config, [field]: value },
          }
        : prev,
    )
  }

  // Actualizar conexión con los valores del formulario
  const updateConnection = (field, value) => {
    const updatedForm = { ...connectionForm, [field]: value }
    setConnectionForm(updatedForm)

    // Actualizar la conexión en el estado
    setConnections((prev) =>
      prev.map((conn) => (conn.id === selectedConnection.id ? { ...conn, [field]: value } : conn)),
    )

    // Actualizar selectedConnection
    setSelectedConnection((prev) => (prev ? { ...prev, [field]: value } : prev))
  }

  const deleteNode = (nodeId) => {
    setNodes((prev) => prev.filter((node) => node.id !== nodeId))
    setConnections((prev) => prev.filter((conn) => conn.from !== nodeId && conn.to !== nodeId))
    setSelectedNode(null)
  }

  const deleteConnection = (connectionId) => {
    setConnections((prev) => prev.filter((conn) => conn.id !== connectionId))
    setSelectedConnection(null)
  }

  const getNodeIcon = (type) => {
    const nodeType = nodeTypes.find((n) => n.type === type)
    return nodeType?.icon || Circle
  }

  const getNodeColor = (type) => {
    const nodeType = nodeTypes.find((n) => n.type === type)
    return nodeType?.color || "bg-gray-500"
  }

  const saveAutomation = async () => {
    setIsLoading(true)

    // Simular tiempo de guardado
    await new Promise((resolve) => setTimeout(resolve, 1200))

    const automation = {
      id: editingAutomation?.id || `auto_${String(Date.now()).slice(-3)}`,
      name: automationName,
      description: automationDescription,
      nodeCount: nodes.length,
      connectionCount: connections.length,
      createdAt: editingAutomation?.createdAt || new Date(),
      updatedAt: new Date(),
      status: editingAutomation?.status || "draft",
      executions: editingAutomation?.executions || 0,
      successRate: editingAutomation?.successRate || 0,
      category: editingAutomation?.category || "Personalizada",
      priority: editingAutomation?.priority || "media",
      executionTime: "--",
      nextRun: null,
      nodesData: JSON.stringify(nodes),
      connectionsData: JSON.stringify(connections),
    }

    if (editingAutomation) {
      setSavedAutomations((prev) => prev.map((a) => (a.id === editingAutomation.id ? automation : a)))
      setEditingAutomation(null)
    } else {
      setSavedAutomations((prev) => [...prev, automation])
    }

    setLastSaved(new Date())
    setIsLoading(false)

    // Reset después de guardar
    setTimeout(() => {
      setAutomationName("Nueva Automatización")
      setAutomationDescription("")
      setNodes([])
      setConnections([])
      setSelectedNode(null)
      setSelectedConnection(null)
      setActiveView("automations")
    }, 500)
  }

  const loadAutomation = (automation) => {
    setEditingAutomation(automation)
    setAutomationName(automation.name)
    setAutomationDescription(automation.description)

    if (automation.nodesData) {
      try {
        setNodes(JSON.parse(automation.nodesData))
      } catch (e) {
        console.error("Error loading nodes:", e)
      }
    }
    if (automation.connectionsData) {
      try {
        setConnections(JSON.parse(automation.connectionsData))
      } catch (e) {
        console.error("Error loading connections:", e)
      }
    }

    setActiveView("editor")
    setSelectedNode(null)
    setSelectedConnection(null)
  }

  const toggleAutomationStatus = async (automationId) => {
    const automation = savedAutomations.find((a) => a.id === automationId)
    if (!automation) return

    setIsLoading(true)

    // Simular procesamiento
    await new Promise((resolve) => setTimeout(resolve, 800))

    setSavedAutomations((prev) =>
      prev.map((a) => {
        if (a.id === automationId) {
          const newStatus = a.status === "active" ? "paused" : a.status === "paused" ? "active" : "active"

          const updates = {
            status: newStatus,
            updatedAt: new Date(),
          }

          if (newStatus === "active" && a.status !== "active") {
            updates.lastRun = new Date()
            updates.executions = a.executions + 1
            updates.nextRun = new Date(Date.now() + 60 * 60 * 1000) // Próxima ejecución en 1 hora
          } else if (newStatus === "paused") {
            updates.nextRun = null
          }

          return { ...a, ...updates }
        }
        return a
      }),
    )

    setIsLoading(false)
  }

  const deleteAutomation = (automationId) => {
    // eslint-disable-next-line no-unused-vars
    const automation = savedAutomations.find((a) => a.id === automationId)

    setSavedAutomations((prev) => prev.filter((a) => a.id !== automationId))
  }

  const handleCanvasClick = () => {
    if (connectionMode) {
      setConnectingFrom(null)
      setConnectionMode(false)
    }
    setSelectedNode(null)
    setSelectedConnection(null)
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "text-green-700 bg-green-100 border-green-200"
      case "paused":
        return "text-orange-700 bg-orange-100 border-orange-200"
      case "draft":
        return "text-gray-700 bg-gray-100 border-gray-200"
      default:
        return "text-gray-700 bg-gray-100 border-gray-200"
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case "active":
        return CheckCircle
      case "paused":
        return Pause
      case "draft":
        return Edit
      default:
        return Circle
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case "active":
        return "Activa"
      case "paused":
        return "Pausada"
      case "draft":
        return "Borrador"
      default:
        return "Desconocido"
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "alta":
        return "text-red-600 bg-red-50"
      case "media":
        return "text-yellow-600 bg-yellow-50"
      case "baja":
        return "text-green-600 bg-green-50"
      default:
        return "text-gray-600 bg-gray-50"
    }
  }

  // Calcular estadísticas
  const stats = {
    total: savedAutomations.length,
    active: savedAutomations.filter((a) => a.status === "active").length,
    paused: savedAutomations.filter((a) => a.status === "paused").length,
    totalExecutions: savedAutomations.reduce((sum, a) => sum + a.executions, 0),
    avgSuccess:
      savedAutomations.length > 0
        ? Math.round(savedAutomations.reduce((sum, a) => sum + a.successRate, 0) / savedAutomations.length)
        : 0,
    highPriority: savedAutomations.filter((a) => a.priority === "alta").length,
  }

  return (
    <div className="automation-container">
      {/* Loading Overlay */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-content">
            <Loader2 className="loading-spinner" />
            <span>Procesando...</span>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <div className={`automation-sidebar ${isSidebarCollapsed ? "collapsed" : ""}`}>
        <div className="sidebar-header">
          <div className="logo">
            <div className="logo-icon">
              <Zap className="icon" />
            </div>
            {!isSidebarCollapsed && (
              <div className="logo-text">
                <h1>Automatización</h1>
                <p>Sistema de Gestión</p>
              </div>
            )}
          </div>
          <button className="toggle-sidebar-button" onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}>
            {isSidebarCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>
        </div>

        <div className="nav-tabs-sidebar">
          <button
            onClick={() => setActiveView("automations")}
            className={`nav-tab-sidebar ${activeView === "automations" ? "active" : ""}`}
          >
            <Layers className="nav-icon" />
            {!isSidebarCollapsed && <span>Gestión</span>}
          </button>
          <button
            onClick={() => setActiveView("editor")}
            className={`nav-tab-sidebar ${activeView === "editor" ? "active" : ""}`}
          >
            <Settings className="nav-icon" />
            {!isSidebarCollapsed && <span>Editor</span>}
          </button>
        </div>

        {!isSidebarCollapsed && (
          <>
            {activeView === "editor" && (
              <>
                <div className="search-section">
                  <div className="search-wrapper">
                    <Search className="search-icon" />
                    <input
                      placeholder="Buscar componentes..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input"
                    />
                  </div>
                </div>

                <div className="categories-section">
                  <div className="categories-tabs">
                    {["triggers", "conditions", "actions", "notifications", "helpers"].map((category) => (
                      <button
                        key={category}
                        onClick={() => setActiveCategory(category)}
                        className={`category-tab ${activeCategory === category ? "active" : ""}`}
                      >
                        {category === "triggers" ? "TRIGGERS" : category.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="components-section">
                  <div className="components-list">
                    {filteredNodes.map((nodeType) => {
                      const Icon = nodeType.icon
                      return (
                        <div
                          key={nodeType.type}
                          draggable
                          onDragStart={() => handleDragStart(nodeType.type)}
                          className="component-item"
                        >
                          <div className="component-content">
                            <div className={`component-icon ${nodeType.color}`}>
                              <Icon className="icon" />
                            </div>
                            <div className="component-info">
                              <p className="component-name">{nodeType.name}</p>
                              <p className="component-description">{nodeType.description}</p>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                <div className="automation-info">
                  <div className="info-field">
                    <label>Nombre</label>
                    <input
                      value={automationName}
                      onChange={(e) => setAutomationName(e.target.value)}
                      className="info-input"
                      placeholder="Nombre de automatización"
                    />
                  </div>
                  <div className="info-field">
                    <label>Descripción</label>
                    <textarea
                      value={automationDescription}
                      onChange={(e) => setAutomationDescription(e.target.value)}
                      className="info-textarea"
                      placeholder="Describe qué hace esta automatización"
                    />
                  </div>
                  {lastSaved && (
                    <div className="last-saved">
                      <CheckCircle size={12} />
                      <span>Guardado {lastSaved.toLocaleTimeString()}</span>
                    </div>
                  )}
                </div>
              </>
            )}

            {activeView === "automations" && (
              <>
                <div className="search-section">
                  <div className="search-wrapper">
                    <Search className="search-icon" />
                    <input
                      placeholder="Buscar automatizaciones..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input"
                    />
                  </div>
                </div>

                <div className="categories-section">
                  <h4 className="section-title">Resumen General</h4>
                  <div className="summary-stats">
                    <div className="summary-item">
                      <div className="summary-icon bg-green-500">
                        <CheckCircle className="icon" />
                      </div>
                      <div className="summary-info">
                        <span className="summary-value">{stats.active}</span>
                        <span className="summary-label">Activas</span>
                      </div>
                    </div>
                    <div className="summary-item">
                      <div className="summary-icon bg-orange-500">
                        <Pause className="icon" />
                      </div>
                      <div className="summary-info">
                        <span className="summary-value">{stats.paused}</span>
                        <span className="summary-label">Pausadas</span>
                      </div>
                    </div>
                    <div className="summary-item">
                      <div className="summary-icon bg-blue-500">
                        <TrendingUp className="icon" />
                      </div>
                      <div className="summary-info">
                        <span className="summary-value">{stats.avgSuccess}%</span>
                        <span className="summary-label">Éxito</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="categories-section">
                  <h4 className="section-title">Filtrar por Estado</h4>
                  <div className="categories-tabs">
                    {["todas", "active", "paused", "draft"].map((status) => (
                      <button
                        key={status}
                        onClick={() => setActiveCategory(status)}
                        className={`category-tab ${activeCategory === status ? "active" : ""}`}
                      >
                        {status === "todas" ? "TODAS" : 
                         status === "active" ? "ACTIVAS" : 
                         status === "paused" ? "PAUSADAS" : "BORRADORES"}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="categories-section">
                  <h4 className="section-title">Por Categoría</h4>
                  <div className="filter-list">
                    {["Control Ambiental", "Inventario", "Logística", "Mantenimiento", "Seguridad", "Optimización", "Calidad"].map((category) => (
                      <div key={category} className="filter-item">
                        <span className="filter-name">{category}</span>
                        <span className="filter-count">
                          {savedAutomations.filter(a => a.category === category).length}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="automation-info">
                  <div className="info-field">
                    <div className="priority-alerts">
                      <h4 className="section-title">Alertas Prioritarias</h4>
                      <div className="alert-item">
                        <div className="alert-icon bg-red-500">
                          <Bell className="icon" />
                        </div>
                        <div className="alert-content">
                          <span className="alert-text">2 automatizaciones requieren atención</span>
                        </div>
                      </div>
                      <div className="alert-item">
                        <div className="alert-icon bg-yellow-500">
                          <Clock className="icon" />
                        </div>
                        <div className="alert-content">
                          <span className="alert-text">5 próximas ejecuciones en 1h</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Main Content */}
      <div className="automation-content">
        {activeView === "editor" && (
          <>
            <div className="automation-toolbar">
              <div className="toolbar-left">
                <div className="toolbar-info">
                  <span className="toolbar-item">{nodes.length} nodos</span>
                  <span className="toolbar-item">{connections.length} conexiones</span>
                  <span className="status-badge">{editingAutomation ? "Editando" : "Borrador"}</span>
                </div>
              </div>
              <div className="toolbar-right">
                <button
                  onClick={() => {
                    setConnectionMode(!connectionMode)
                    setConnectingFrom(null)
                  }}
                  className={`toolbar-btn ${connectionMode ? "active" : ""}`}
                >
                  <Link size={16} />
                  {connectionMode ? "Cancelar" : "Conectar"}
                </button>
                <button className="toolbar-btn" onClick={() => setActiveView("automations")}>
                  <Eye size={16} />
                  Ver Todas
                </button>
                <button className="toolbar-btn save" onClick={saveAutomation} disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      {editingAutomation ? "Actualizar" : "Guardar"}
                    </>
                  )}
                </button>
              </div>
            </div>

            <div
              ref={canvasRef}
              className="automation-canvas"
              onDrop={handleCanvasDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={handleCanvasClick}
            >
              <svg className="connections-svg">
                {createArrowMarker()}
                {connections.map((connection) => {
                  const fromNode = nodes.find((n) => n.id === connection.from)
                  const toNode = nodes.find((n) => n.id === connection.to)

                  if (!fromNode || !toNode) return null

                  return (
                    <g key={connection.id}>
                      <path
                        d={getConnectionPath(fromNode, toNode)}
                        stroke="#6b7280"
                        strokeWidth="2"
                        strokeDasharray="5,5"
                        fill="none"
                        markerEnd="url(#arrowhead)"
                        className={`connection-path ${selectedConnection?.id === connection.id ? "selected" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedConnection(connection)
                          setSelectedNode(null)
                        }}
                      />
                      {connection.label && (
                        <text
                          x={(fromNode.position.x + toNode.position.x) / 2 + 75}
                          y={(fromNode.position.y + toNode.position.y) / 2 + 25 - 25}
                          textAnchor="middle"
                          className="connection-label"
                        >
                          {connection.label}
                        </text>
                      )}
                    </g>
                  )
                })}
              </svg>

              {nodes.map((node) => {
                const Icon = getNodeIcon(node.type)
                const isConnecting = connectingFrom === node.id
                return (
                  <div
                    key={node.id}
                    className={`automation-node ${selectedNode?.id === node.id ? "selected" : ""} ${isConnecting ? "connecting" : ""}`}
                    style={{
                      left: node.position.x,
                      top: node.position.y,
                    }}
                    onMouseDown={(e) => handleNodeMouseDown(node, e)}
                    onClick={(e) => handleNodeClick(node, e)}
                  >
                    <div className="node-content">
                      <div className="node-header">
                        <div className={`node-icon ${getNodeColor(node.type)}`}>
                          <Icon className="icon" />
                        </div>
                        <span className="node-title">{node.name}</span>
                      </div>
                      <p className="node-description">{node.description}</p>

                      {Object.keys(node.config).length > 0 && (
                        <div className="node-config">
                          {Object.entries(node.config).map(([key, value]) => (
                            <div key={key} className="config-item">
                              {key}: {String(value)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}

              {nodes.length === 0 && (
                <div className="help-message">
                  <div className="help-content">
                    <div className="help-icon">⚡</div>
                    <h3>Construye tu automatización</h3>
                    <p>Arrastra componentes desde el panel lateral para empezar</p>
                    <p className="help-tip">💡 Usa "Conectar" para relacionar los componentes</p>
                    <p className="help-tip">🔗 Cualquier componente puede conectarse con cualquier otro</p>
                  </div>
                </div>
              )}

              {connectionMode && (
                <div className="connection-mode-indicator">
                  <p>{connectingFrom ? "Selecciona el componente destino" : "Selecciona el componente origen"}</p>
                </div>
              )}
            </div>
          </>
        )}

        {activeView === "automations" && (
          <div className="automations-overview">
            <div className="overview-header">
              <div className="header-content">
                <h2>Automatizaciones</h2>
                <p>Gestiona y supervisa todas tus automatizaciones en tiempo real</p>
              </div>
              <button
                className="new-automation-btn"
                onClick={() => {
                  setEditingAutomation(null)
                  setAutomationName("Nueva Automatización")
                  setAutomationDescription("")
                  setNodes([])
                  setConnections([])
                  setActiveView("editor")
                }}
              >
                <Plus size={16} />
                Nueva Automatización
              </button>
            </div>

            <div className="overview-stats">
              <div className="stat-card">
                <div className="stat-icon">
                  <Layers size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{stats.total}</div>
                  <div className="stat-label">Total Automatizaciones</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon active">
                  <PlayCircle size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{stats.active}</div>
                  <div className="stat-label">Activas</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">
                  <TrendingUp size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{stats.totalExecutions.toLocaleString()}</div>
                  <div className="stat-label">Ejecuciones Totales</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">
                  <Target size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{stats.avgSuccess}%</div>
                  <div className="stat-label">Éxito Promedio</div>
                </div>
              </div>
            </div>

            <div className="automations-grid">
              {savedAutomations.map((automation) => {
                const StatusIcon = getStatusIcon(automation.status)
                return (
                  <div key={automation.id} className="automation-card">
                    <div className="card-header">
                      <div className="card-title-section">
                        <div className="card-title">
                          <h3>{automation.name}</h3>
                          <span className={`status-badge ${getStatusColor(automation.status)}`}>
                            <StatusIcon size={12} />
                            {getStatusText(automation.status)}
                          </span>
                        </div>
                        <div className="card-meta">
                          <span className="card-id">#{automation.id}</span>
                          <span className={`priority-badge ${getPriorityColor(automation.priority)}`}>
                            {automation.priority}
                          </span>
                          <span className="card-category">{automation.category}</span>
                        </div>
                      </div>
                    </div>

                    <p className="card-description">{automation.description}</p>

                    <div className="card-metrics">
                      <div className="metric-group">
                        <div className="metric">
                          <span className="metric-label">Componentes:</span>
                          <span className="metric-value">
                            {automation.nodeCount}N • {automation.connectionCount}C
                          </span>
                        </div>
                        <div className="metric">
                          <span className="metric-label">Rendimiento:</span>
                          <span className="metric-value">
                            {automation.executions} • {automation.successRate}%
                          </span>
                        </div>
                      </div>
                      <div className="metric-group">
                        <div className="metric">
                          <span className="metric-label">Tiempo ejecución:</span>
                          <span className="metric-value">{automation.executionTime}</span>
                        </div>
                        {automation.nextRun && (
                          <div className="metric">
                            <span className="metric-label">Próxima ejecución:</span>
                            <span className="metric-value next-run">{automation.nextRun.toLocaleTimeString()}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {automation.lastRun && (
                      <div className="card-last-run">
                        <Clock size={12} />
                        <span>Última ejecución: {automation.lastRun.toLocaleString()}</span>
                      </div>
                    )}

                    <div className="card-actions">
                      <button className="card-action edit" onClick={() => loadAutomation(automation)}>
                        <Edit size={14} />
                        Editar
                      </button>
                      <button
                        className="card-action play"
                        onClick={() => toggleAutomationStatus(automation.id)}
                        disabled={isLoading}
                      >
                        {automation.status === "active" ? (
                          <>
                            <Pause size={14} />
                            Pausar
                          </>
                        ) : (
                          <>
                            <Play size={14} />
                            {automation.status === "draft" ? "Activar" : "Reanudar"}
                          </>
                        )}
                      </button>
                      <button className="card-action delete" onClick={() => deleteAutomation(automation.id)}>
                        <Trash2 size={14} />
                        Eliminar
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>

      {/* Configuration Panel mejorado */}
      {(selectedNode || selectedConnection) && (
        <div className="config-panel">
          <div className="config-header">
            <h3>{selectedNode ? "Configuración de Componente" : "Configuración de Conexión"}</h3>
            <button
              className="close-config"
              onClick={() => {
                setSelectedNode(null)
                setSelectedConnection(null)
              }}
            >
              <X size={18} />
            </button>
          </div>

          {selectedNode && (
            <div className="config-content">
              <div className="config-section">
                <h4>Información General</h4>
                <div className="config-field">
                  <label>Nombre</label>
                  <input
                    value={nodeForm.name}
                    onChange={(e) => updateNode("name", e.target.value)}
                    placeholder="Nombre del componente"
                  />
                </div>

                <div className="config-field">
                  <label>Descripción</label>
                  <textarea
                    value={nodeForm.description}
                    onChange={(e) => updateNode("description", e.target.value)}
                    placeholder="Describe qué hace este componente"
                  />
                </div>
              </div>

              {/* Configuración específica por tipo */}
              <div className="config-section">
                <h4>Configuración Específica</h4>

                {selectedNode.type === "low_stock" && (
                  <div className="config-field">
                    <label>Umbral Mínimo (unidades)</label>
                    <input
                      type="number"
                      value={nodeForm.config.threshold || ""}
                      onChange={(e) => updateNode("threshold", e.target.value)}
                      placeholder="ej: 50"
                    />
                  </div>
                )}

                {selectedNode.type === "temperature_alert" && (
                  <>
                    <div className="config-field">
                      <label>Temperatura Mínima (°C)</label>
                      <input
                        type="number"
                        value={nodeForm.config.minTemp || ""}
                        onChange={(e) => updateNode("minTemp", e.target.value)}
                        placeholder="ej: -18"
                      />
                    </div>
                    <div className="config-field">
                      <label>Temperatura Máxima (°C)</label>
                      <input
                        type="number"
                        value={nodeForm.config.maxTemp || ""}
                        onChange={(e) => updateNode("maxTemp", e.target.value)}
                        placeholder="ej: -15"
                      />
                    </div>
                  </>
                )}

                {(selectedNode.type === "move_product" || selectedNode.type === "location_check") && (
                  <div className="config-field">
                    <label>Ubicación Destino</label>
                    <select
                      value={nodeForm.config.location || ""}
                      onChange={(e) => updateNode("location", e.target.value)}
                    >
                      <option value="">Seleccionar ubicación</option>
                      <option value="zona-a1">Zona A1 - Productos Fríos</option>
                      <option value="zona-b2">Zona B2 - Secos</option>
                      <option value="zona-c3">Zona C3 - Congelados</option>
                      <option value="muelle-01">Muelle 01 - Carga</option>
                      <option value="muelle-02">Muelle 02 - Descarga</option>
                    </select>
                  </div>
                )}

                {selectedNode.type === "assign_task" && (
                  <>
                    <div className="config-field">
                      <label>Tipo de Tarea</label>
                      <select
                        value={nodeForm.config.taskType || ""}
                        onChange={(e) => updateNode("taskType", e.target.value)}
                      >
                        <option value="">Seleccionar tarea</option>
                        <option value="picking">Preparación de Pedidos</option>
                        <option value="inventory">Control de Inventario</option>
                        <option value="maintenance">Mantenimiento Preventivo</option>
                        <option value="receiving">Recepción de Mercancía</option>
                        <option value="cleaning">Limpieza de Zona</option>
                      </select>
                    </div>
                    <div className="config-field">
                      <label>Operador Asignado</label>
                      <select
                        value={nodeForm.config.operator || ""}
                        onChange={(e) => updateNode("operator", e.target.value)}
                      >
                        <option value="">Auto-asignar</option>
                        <option value="op001">Carlos Martínez</option>
                        <option value="op002">Ana López</option>
                        <option value="op003">Miguel Torres</option>
                        <option value="team-a">Equipo A - Mañana</option>
                        <option value="team-b">Equipo B - Tarde</option>
                      </select>
                    </div>
                  </>
                )}

                {(selectedNode.type === "notification" ||
                  selectedNode.type === "email" ||
                  selectedNode.type === "dashboard_alert") && (
                  <>
                    <div className="config-field">
                      <label>Mensaje</label>
                      <textarea
                        value={nodeForm.config.message || ""}
                        onChange={(e) => updateNode("message", e.target.value)}
                        placeholder="Escribe el mensaje de la notificación..."
                      />
                    </div>
                    <div className="config-field">
                      <label>Destinatarios</label>
                      <select
                        value={nodeForm.config.recipients || ""}
                        onChange={(e) => updateNode("recipients", e.target.value)}
                      >
                        <option value="">Seleccionar destinatarios</option>
                        <option value="supervisores">Supervisores</option>
                        <option value="operadores">Operadores</option>
                        <option value="mantenimiento">Equipo Mantenimiento</option>
                        <option value="gerencia">Gerencia</option>
                        <option value="todos">Todos</option>
                      </select>
                    </div>
                  </>
                )}

                {selectedNode.type === "delay" && (
                  <div className="config-field">
                    <label>Tiempo de Espera</label>
                    <div className="input-group">
                      <input
                        type="number"
                        value={nodeForm.config.delay || ""}
                        onChange={(e) => updateNode("delay", e.target.value)}
                        placeholder="5"
                      />
                      <select
                        value={nodeForm.config.delayUnit || "minutes"}
                        onChange={(e) => updateNode("delayUnit", e.target.value)}
                      >
                        <option value="seconds">Segundos</option>
                        <option value="minutes">Minutos</option>
                        <option value="hours">Horas</option>
                      </select>
                    </div>
                  </div>
                )}

                {selectedNode.type === "schedule_trigger" && (
                  <>
                    <div className="config-field">
                      <label>Horario de Ejecución</label>
                      <input
                        type="time"
                        value={nodeForm.config.time || ""}
                        onChange={(e) => updateNode("time", e.target.value)}
                      />
                    </div>
                    <div className="config-field">
                      <label>Días de la Semana</label>
                      <div className="checkbox-group">
                        {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((day, index) => (
                          <label key={day} className="checkbox-item">
                            <input
                              type="checkbox"
                              checked={(nodeForm.config.days || []).includes(index)}
                              onChange={(e) => {
                                const days = nodeForm.config.days || []
                                const newDays = e.target.checked ? [...days, index] : days.filter((d) => d !== index)
                                updateNode("days", newDays)
                              }}
                            />
                            <span>{day}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="config-separator" />
              <button className="delete-node-btn" onClick={() => deleteNode(selectedNode.id)}>
                <Trash2 size={16} />
                Eliminar Componente
              </button>
            </div>
          )}

          {selectedConnection && (
            <div className="config-content">
              <div className="config-section">
                <h4>Información de la Conexión</h4>
                <div className="connection-info">
                  <p>
                    <strong>Desde:</strong> {nodes.find((n) => n.id === selectedConnection.from)?.name}
                  </p>
                  <p>
                    <strong>Hacia:</strong> {nodes.find((n) => n.id === selectedConnection.to)?.name}
                  </p>
                </div>
              </div>

              <div className="config-section">
                <h4>Configuración</h4>
                <div className="config-field">
                  <label>Etiqueta de Conexión</label>
                  <input
                    value={connectionForm.label}
                    onChange={(e) => updateConnection("label", e.target.value)}
                    placeholder="ej: Si es verdadero, Cuando se completa"
                  />
                </div>

                <div className="config-field">
                  <label>Condición de Ejecución</label>
                  <textarea
                    value={connectionForm.condition}
                    onChange={(e) => updateConnection("condition", e.target.value)}
                    placeholder="Describe cuándo debe ejecutarse esta conexión..."
                  />
                </div>
              </div>

              <div className="config-separator" />
              <button className="delete-connection-btn" onClick={() => deleteConnection(selectedConnection.id)}>
                <Trash2 size={16} />
                Eliminar Conexión
              </button>
            </div>
          )}
        </div>
      )}

      <style jsx>{`
        .automation-container {
          display: flex;
          height: 100vh;
          background: #f8fafc;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          position: relative;
        }

        /* Loading Overlay */
        .loading-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .loading-content {
          display: flex;
          align-items: center;
          gap: 12px;
          background: white;
          padding: 20px 32px;
          border-radius: 12px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
          font-size: 16px;
          font-weight: 500;
          color: #374151;
        }

        .loading-spinner {
          animation: spin 1s linear infinite;
          color: #3b82f6;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .animate-spin {
          animation: spin 1s linear infinite;
        }

        /* Sidebar Styles */
        .automation-sidebar {
          width: 300px;
          background: linear-gradient(180deg, #1a202c 0%, #2d3748 100%);
          display: flex;
          flex-direction: column;
          position: sticky;
          top: 0;
          height: 100vh;
          overflow-y: auto;
          transition: width 0.3s ease;
          flex-shrink: 0;
          z-index: 10;
        }

        .automation-sidebar.collapsed {
          width: 70px;
        }

        .sidebar-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 16px;
          min-height: 80px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          position: relative;
        }

        .logo {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
          flex: 1;
        }

        .logo-icon {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
          flex-shrink: 0;
        }

        .logo-icon .icon {
          color: white;
          width: 22px;
          height: 22px;
        }

        .logo-text {
          overflow: hidden;
        }

        .logo-text h1 {
          color: white;
          font-size: 18px;
          font-weight: 700;
          margin: 0 0 2px 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .logo-text p {
          color: #a0aec0;
          font-size: 13px;
          margin: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* Fixed toggle button positioning */
        .toggle-sidebar-button {
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
          border-radius: 6px;
          transition: background 0.3s ease;
          color: #94a3b8;
          flex-shrink: 0;
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translateY(-50%);
          z-index: 20;
        }

        .toggle-sidebar-button:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .nav-tabs-sidebar {
          display: flex;
          margin: 0 16px 16px;
          gap: 4px;
        }

        .nav-tab-sidebar {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px;
          border: none;
          background: none;
          color: #a0aec0;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          border-radius: 8px;
          position: relative;
          min-width: 0;
        }

        .nav-tab-sidebar span {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .nav-tab-sidebar:hover {
          background: rgba(255, 255, 255, 0.1);
          color: white;
        }

        .nav-tab-sidebar.active {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }

        .nav-icon {
          width: 16px;
          height: 16px;
          flex-shrink: 0;
        }

        .search-section {
          padding: 16px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .search-wrapper {
          position: relative;
        }

        .search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
          width: 16px;
          height: 16px;
        }

        .search-input {
          width: 100%;
          padding: 10px 12px 10px 36px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          font-size: 14px;
          outline: none;
          background: rgba(255, 255, 255, 0.1);
          color: white;
          transition: border-color 0.2s ease;
          box-sizing: border-box;
        }

        .search-input::placeholder {
          color: #a0aec0;
        }

        .search-input:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 1px #667eea;
        }

        .categories-section {
          padding: 16px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .categories-tabs {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }

        .category-tab {
          padding: 6px 10px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 14px;
          background: rgba(255, 255, 255, 0.1);
          color: #a0aec0;
          font-size: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .category-tab.active {
          background: #667eea;
          border-color: #667eea;
          color: white;
        }

        .category-tab:hover:not(.active) {
          border-color: rgba(255, 255, 255, 0.4);
          background: rgba(255, 255, 255, 0.15);
          color: white;
        }

        .components-section {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
        }

        .components-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .component-item {
          padding: 12px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.05);
          cursor: move;
          transition: all 0.2s ease;
        }

        .component-item:hover {
          border-color: #667eea;
          background: rgba(102, 126, 234, 0.1);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
        }

        .component-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .component-icon {
          width: 32px;
          height: 32px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
        }

        .component-icon .icon {
          width: 16px;
          height: 16px;
        }

        .component-info {
          flex: 1;
          min-width: 0;
        }

        .component-name {
          font-size: 13px;
          font-weight: 600;
          color: white;
          margin: 0 0 2px 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .component-description {
          font-size: 11px;
          color: #a0aec0;
          margin: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .automation-info {
          padding: 16px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .info-field {
          margin-bottom: 16px;
        }

        .info-field:last-child {
          margin-bottom: 0;
        }

        .info-field label {
          display: block;
          font-size: 12px;
          font-weight: 600;
          color: white;
          margin-bottom: 6px;
        }

        .info-input, .info-textarea {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 6px;
          font-size: 13px;
          outline: none;
          background: rgba(255, 255, 255, 0.1);
          color: white;
          transition: border-color 0.2s ease;
          box-sizing: border-box;
        }

        .info-input::placeholder, .info-textarea::placeholder {
          color: #a0aec0;
        }

        .info-textarea {
          height: 70px;
          resize: vertical;
        }

        .info-input:focus, .info-textarea:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 1px #667eea;
        }

        .last-saved {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          color: #10b981;
          margin-top: 8px;
        }

        /* Content Styles */
        .automation-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .automation-toolbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          background: #ffffff;
          border-bottom: 1px solid #e5e7eb;
          flex-shrink: 0;
        }

        .toolbar-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .toolbar-info {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .toolbar-item {
          font-size: 14px;
          color: #6b7280;
          padding: 4px 8px;
          background: #f3f4f6;
          border-radius: 6px;
        }

        .status-badge {
          font-size: 12px;
          color: #d97706;
          background: #fef3c7;
          padding: 4px 8px;
          border-radius: 6px;
          border: 1px solid #fde68a;
        }

        .toolbar-right {
          display: flex;
          gap: 8px;
        }

        .toolbar-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          background: #ffffff;
          color: #374151;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .toolbar-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .toolbar-btn:hover:not(:disabled) {
          border-color: #9ca3af;
          background: #f9fafb;
        }

        .toolbar-btn.active {
          background: #3b82f6;
          border-color: #3b82f6;
          color: white;
        }

        .toolbar-btn.save {
          background: #10b981;
          border-color: #10b981;
          color: white;
        }

        .toolbar-btn.save:hover:not(:disabled) {
          background: #059669;
          border-color: #059669;
        }

        .automation-canvas {
          flex: 1;
          position: relative;
          background: #f8fafc;
          background-image: radial-gradient(circle, #e5e7eb 1px, transparent 1px);
          background-size: 20px 20px;
          overflow: auto;
          cursor: default;
        }

        .connections-svg {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 1;
        }

        .connection-path {
          cursor: pointer;
          pointer-events: all;
          transition: stroke 0.2s ease;
        }

        .connection-path:hover {
          stroke: #3b82f6;
          stroke-width: 3;
        }

        .connection-path.selected {
          stroke: #3b82f6;
          stroke-width: 3;
        }

        .connection-label {
          font-size: 12px;
          fill: #6b7280;
          pointer-events: none;
        }

        .automation-node {
          position: absolute;
          cursor: move;
          z-index: 2;
          transition: all 0.2s ease;
          min-width: 150px;
          max-width: 200px;
        }

        .automation-node:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .automation-node.selected {
          box-shadow: 0 0 0 2px #3b82f6;
        }

        .automation-node.connecting {
          box-shadow: 0 0 0 2px #10b981;
        }

        .section-title {
          font-size: 12px;
          font-weight: 700;
          color: white;
          margin: 0 0 12px 0;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        /* Resumen General - Estadísticas */
        .summary-stats {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 8px;
        }

        .summary-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: all 0.2s ease;
        }

        .summary-item:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.2);
        }

        .summary-icon {
          width: 28px;
          height: 28px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .summary-icon .icon {
          width: 14px;
          height: 14px;
          color: white;
        }

        .summary-info {
          flex: 1;
          min-width: 0;
        }

        .summary-value {
          font-size: 16px;
          font-weight: 700;
          color: white;
          display: block;
          line-height: 1.2;
        }

        .summary-label {
          font-size: 11px;
          color: #a0aec0;
          font-weight: 500;
        }

        /* Lista de filtros por categoría */
        .filter-list {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .filter-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 12px;
          border-radius: 6px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .filter-item:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.2);
          transform: translateX(2px);
        }

        .filter-name {
          font-size: 12px;
          font-weight: 500;
          color: white;
          flex: 1;
        }

        .filter-count {
          font-size: 11px;
          font-weight: 600;
          color: #a0aec0;
          background: rgba(255, 255, 255, 0.1);
          padding: 2px 6px;
          border-radius: 10px;
          min-width: 20px;
          text-align: center;
        }

        /* Automatizaciones recientes */
        .recent-automations {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .recent-automation-item {
          padding: 12px;
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .recent-automation-item:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.2);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }

        .recent-automation-content {
          width: 100%;
        }

        .recent-automation-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 8px;
          margin-bottom: 8px;
        }

        .recent-automation-name {
          font-size: 13px;
          font-weight: 600;
          color: white;
          line-height: 1.3;
          flex: 1;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .recent-status-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .recent-status-badge.bg-green-500 {
          background: #10b981;
        }

        .recent-status-badge.bg-orange-500 {
          background: #f59e0b;
        }

        .recent-status-badge.bg-gray-500 {
          background: #6b7280;
        }

        .recent-automation-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 8px;
        }

        .recent-automation-category {
          font-size: 11px;
          color: #a0aec0;
          font-weight: 500;
          flex: 1;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .recent-automation-nodes {
          font-size: 10px;
          font-weight: 600;
          color: #667eea;
          background: rgba(102, 126, 234, 0.2);
          padding: 2px 6px;
          border-radius: 8px;
          border: 1px solid rgba(102, 126, 234, 0.3);
        }

        /* Alertas prioritarias */
        .priority-alerts {
          margin-top: 8px;
        }

        .alert-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          margin-bottom: 8px;
          transition: all 0.2s ease;
        }

        .alert-item:last-child {
          margin-bottom: 0;
        }

        .alert-item:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.2);
          transform: translateX(2px);
        }

        .alert-icon {
          width: 24px;
          height: 24px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .alert-icon.bg-red-500 {
          background: #ef4444;
        }

        .alert-icon.bg-yellow-500 {
          background: #f59e0b;
        }

        .alert-icon .icon {
          width: 12px;
          height: 12px;
          color: white;
        }

        .alert-content {
          flex: 1;
          min-width: 0;
        }

        .alert-text {
          font-size: 11px;
          font-weight: 500;
          color: white;
          line-height: 1.3;
        }

        /* Clases utilitarias para colores de fondo */
        .bg-green-500 {
          background-color: #10b981;
        }

        .bg-orange-500 {
          background-color: #f59e0b;
        }

        .bg-blue-500 {
          background-color: #3b82f6;
        }

        .bg-gray-500 {
          background-color: #6b7280;
        }

        .bg-red-500 {
          background-color: #ef4444;
        }

        .bg-yellow-500 {
          background-color: #f59e0b;
        }

        /* Responsive para la vista de automatizaciones */
        @media (max-width: 768px) {
          .summary-stats {
            gap: 6px;
          }

          .summary-item {
            padding: 8px 10px;
            gap: 10px;
          }

          .summary-icon {
            width: 24px;
            height: 24px;
          }

          .summary-value {
            font-size: 14px;
          }

          .recent-automation-item {
            padding: 10px;
          }

          .recent-automation-name {
            font-size: 12px;
          }

          .alert-item {
            padding: 8px 10px;
            gap: 10px;
          }

          .alert-icon {
            width: 20px;
            height: 20px;
          }
        }

        /* Estados hover mejorados */
        .categories-section:hover .section-title {
          color: #667eea;
          transition: color 0.2s ease;
        }

        .components-section:hover .section-title {
          color: #667eea;
          transition: color 0.2s ease;
        }

        /* Animaciones sutiles */
        .summary-item,
        .filter-item,
        .recent-automation-item,
        .alert-item {
          animation: fadeInUp 0.3s ease-out forwards;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Scrollbar personalizada para las secciones */
        .components-section::-webkit-scrollbar {
          width: 6px;
        }

        .components-section::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 3px;
        }

        .components-section::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.3);
          border-radius: 3px;
        }

        .components-section::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.5);
        }

        .node-content {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 12px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .node-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 6px;
        }

        .node-icon {
          width: 24px;
          height: 24px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .node-icon .icon {
          width: 14px;
          height: 14px;
        }

        .node-title {
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
        }

        .node-description {
          font-size: 12px;
          color: #6b7280;
          margin: 0 0 8px 0;
          line-height: 1.3;
        }

        .node-config {
          border-top: 1px solid #f3f4f6;
          padding-top: 8px;
        }

        .config-item {
          font-size: 11px;
          color: #6b7280;
          margin-bottom: 2px;
        }

        .help-message {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          text-align: center;
          color: #9ca3af;
          pointer-events: none;
        }

        .help-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          background: white;
          padding: 40px;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .help-icon {
          font-size: 48px;
        }

        .help-content h3 {
          font-size: 20px;
          font-weight: 600;
          color: #1f2937;
          margin: 0;
        }

        .help-content p {
          margin: 0;
          font-size: 16px;
          line-height: 1.4;
        }

        .help-tip {
          font-size: 14px !important;
          color: #6b7280 !important;
        }

        .connection-mode-indicator {
          position: absolute;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          background: #dcfce7;
          border: 1px solid #bbf7d0;
          color: #166534;
          padding: 12px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          pointer-events: none;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .automations-overview {
          padding: 24px;
          overflow-y: auto;
          flex: 1;
        }

        .overview-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 32px;
        }

        .header-content h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1f2937;
          margin: 0 0 8px 0;
        }

        .header-content p {
          font-size: 16px;
          color: #6b7280;
          margin: 0;
        }

        .new-automation-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          color: white;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
          white-space: nowrap;
        }

        .new-automation-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
        }

        .overview-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 32px;
        }

        .stat-card {
          display: flex;
          align-items: center;
          gap: 16px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 24px;
          transition: all 0.2s ease;
        }

        .stat-card:hover {
          border-color: #d1d5db;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f3f4f6;
          color: #6b7280;
        }

        .stat-icon.active {
          background: #dcfce7;
          color: #16a34a;
        }

        .stat-content {
          flex: 1;
        }

        .stat-value {
          font-size: 28px;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 4px;
        }

        .stat-label {
          font-size: 14px;
          color: #6b7280;
        }

        .automations-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 20px;
        }

        .automation-card {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 24px;
          transition: all 0.2s ease;
        }

        .automation-card:hover {
          border-color: #d1d5db;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }

        .card-header {
          margin-bottom: 16px;
        }

        .card-title-section {
          margin-bottom: 12px;
        }

        .card-title {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 8px;
        }

        .card-title h3 {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          margin: 0;
          flex: 1;
        }

        .status-badge {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
          white-space: nowrap;
          border: 1px solid;
        }

        .card-meta {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .card-id {
          font-size: 12px;
          color: #6b7280;
          font-family: 'Courier New', monospace;
        }

        .priority-badge, .card-category {
          font-size: 11px;
          padding: 2px 6px;
          border-radius: 4px;
          font-weight: 500;
        }

        .card-category {
          background: #f3f4f6;
          color: #6b7280;
        }

        .card-description {
          font-size: 14px;
          color: #6b7280;
          margin: 0 0 20px 0;
          line-height: 1.5;
        }

        .card-metrics {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 16px;
        }

        .metric-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .metric {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 13px;
        }

        .metric-label {
          color: #6b7280;
          font-weight: 500;
        }

        .metric-value {
          font-weight: 600;
          color: #1f2937;
          font-family: 'Courier New', monospace;
        }

        .metric-value.next-run {
          color: #f59e0b;
        }

        .card-last-run {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: #6b7280;
          margin-bottom: 20px;
          padding: 8px;
          background: #f9fafb;
          border-radius: 6px;
        }

        .card-actions {
          display: flex;
          gap: 8px;
        }

        .card-action {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          background: #ffffff;
          color: #374151;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          flex: 1;
          justify-content: center;
        }

        .card-action:hover {
          border-color: #9ca3af;
          background: #f9fafb;
        }

        .card-action.edit:hover {
          color: #3b82f6;
          border-color: #3b82f6;
        }

        .card-action.play:hover {
          color: #10b981;
          border-color: #10b981;
        }

        .card-action.delete:hover {
          color: #ef4444;
          border-color: #ef4444;
        }

        /* Configuration Panel mejorado */
        .config-panel {
          width: 350px;
          background: #ffffff;
          border-left: 1px solid #e5e7eb;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .config-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .config-header h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
          margin: 0;
        }

        .close-config {
          width: 32px;
          height: 32px;
          border: 1px solid #d1d5db;
          background: #ffffff;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6b7280;
          transition: all 0.2s ease;
        }

        .close-config:hover {
          color: #ef4444;
          border-color: #ef4444;
          background: #fef2f2;
        }

        .config-content {
          flex: 1;
          padding: 20px;
          overflow-y: auto;
        }

        .config-section {
          margin-bottom: 24px;
        }

        .config-section h4 {
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 12px 0;
          padding-bottom: 8px;
          border-bottom: 1px solid #e5e7eb;
        }

        .config-field {
          margin-bottom: 16px;
        }

        .config-field:last-child {
          margin-bottom: 0;
        }

        .config-field label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 6px;
        }

        .config-field input,
        .config-field textarea,
        .config-field select {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          outline: none;
          transition: border-color 0.2s ease;
          box-sizing: border-box;
        }

        .config-field textarea {
          height: 80px;
          resize: vertical;
        }

        .config-field input:focus,
        .config-field textarea:focus,
        .config-field select:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 1px #3b82f6;
        }

        .input-group {
          display: flex;
          gap: 8px;
        }

        .input-group input {
          flex: 2;
        }

        .input-group select {
          flex: 1;
        }

        .checkbox-group {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .checkbox-item {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 10px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 12px;
          font-weight: 500;
        }

        .checkbox-item:hover {
          background: #f9fafb;
          border-color: #9ca3af;
        }

        .checkbox-item input[type="checkbox"]:checked + span {
          color: #3b82f6;
        }

        .config-separator {
          height: 1px;
          background: #e5e7eb;
          margin: 24px 0;
        }

        .connection-info {
          background: #f8fafc;
          padding: 16px;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          margin-bottom: 16px;
        }

        .connection-info p {
          font-size: 13px;
          color: #6b7280;
          margin: 0 0 8px 0;
        }

        .connection-info p:last-child {
          margin-bottom: 0;
        }

        .delete-node-btn,
        .delete-connection-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 12px 16px;
          background: #fee2e2;
          border: 1px solid #fca5a5;
          color: #dc2626;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .delete-node-btn:hover,
        .delete-connection-btn:hover {
          background: #fecaca;
          border-color: #f87171;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .automation-sidebar {
            position: fixed;
            left: -300px;
            z-index: 100;
            height: 100vh;
            transition: left 0.3s ease;
          }

          .automation-sidebar.open {
            left: 0;
          }

          .automation-content {
            width: 100%;
          }

          .config-panel {
            position: fixed;
            right: -350px;
            z-index: 100;
            height: 100vh;
            transition: right 0.3s ease;
          }

          .config-panel.open {
            right: 0;
          }

          .overview-stats {
            grid-template-columns: repeat(2, 1fr);
          }

          .automations-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .automation-sidebar,
          .config-panel {
            width: 100%;
            max-width: 350px;
          }

          .overview-stats {
            grid-template-columns: 1fr;
          }

          .card-metrics {
            gap: 8px;
          }

          .overview-header {
            flex-direction: column;
            align-items: stretch;
            gap: 16px;
          }

          .help-content {
            padding: 24px;
            margin: 20px;
          }

          .help-content h3 {
            font-size: 18px;
          }

          .help-content p {
            font-size: 14px;
          }
        }
      `}</style>
    </div>
  )
}
